import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import { ChatPopContainer } from "~/components/common/chat/ChatPopContainer";
import { DealConstructionScheduleClientCard } from "~/components/common/deals/show/construction_card/DealConstructionScheduleClientCard";
import { DealCard } from "~/components/common/deals/show/deal_card/DealCard";
import { DealVendorNoteCard } from "~/components/common/deals/show/deal_card/DealVendorNoteCard";
import { DealWitnessCard } from "~/components/common/deals/show/deal_card/DealWitnessCard";
import { DealInspectionCandidateDateCard } from "~/components/common/deals/show/inspection_candidate_date_card/DealInspectionCandidateDateCard";
import { DealMain } from "~/components/common/deals/show/main/DealMain";
import { DealRepairCard } from "~/components/common/deals/show/repair_card/DealRepairCard";
import { DealOriginVendorClientCard } from "~/components/common/deals/show/vendor_card/DealOriginVendorClientCard";
import { DealVisitDateClientCard } from "~/components/common/deals/show/visit_date_card/DealVisitDateClientCard";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loader from "~/components/common/parts/loading/Loading";
import { SERVER_ERROR_MESSAGE } from "~/constants/AppConst";
import { useMessages } from "~/hooks/deals/useMessages";
import { useDeals } from "~/hooks/useDeals";
import useItandiAccount from "~/hooks/useItandiAccount";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import { UPDATE_TEMPORARY_UNREAD_MESSAGE } from "~/models/Message";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { isSuccess } from "~/utils/api/api_handler";
import { postTemporaryUnread } from "~/utils/api/deals/Message";
import Styles from "./Page.module.scss";

export function Page() {
	const { notify } = useNotification();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const [isOpen, setIsOpen] = useState(false);
	const { data: deal, mutate: mutateDeal, isLoading } = useDeals().show(id);
	const { data: messages, mutate: mutateMessages } = useMessages().index(id);
	const { data: account } = useItandiAccount().currentAccount();

	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ label: "案件詳細" }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];

	const handleToggleChat = useCallback(() => {
		if (isOpen) {
			setIsOpen(false);
		} else {
			setIsOpen(true);
		}
	}, [isOpen]);

	const handleMutate = useCallback(async () => {
		await mutateDeal();
	}, [mutateDeal]);

	const handleMutateMessage = useCallback(async () => {
		await mutateMessages();
		await mutateDeal();
	}, [mutateDeal, mutateMessages]);

	if (isLoading || deal === null || account === null) {
		return <Loader isLoading />;
	}

	const handleTemporaryUnread = async (): Promise<void> => {
		const result = await postTemporaryUnread(deal.id);
		if (isSuccess(result)) {
			notify({ content: UPDATE_TEMPORARY_UNREAD_MESSAGE, variant: "success" });
			await mutateDeal();
			setIsOpen(false);
			return;
		}
		notify({ content: SERVER_ERROR_MESSAGE, variant: "error" });
	};

	return (
		<ItandiAccountContext.Provider value={account}>
			<DefaultV2>
				<ClientDealLayout deal={deal} items={items} noBackground>
					<div className={Styles.Container}>
						<div className={Styles.LeftCol}>
							<DealOriginVendorClientCard handleToggleChat={handleToggleChat} />
							<DealVendorNoteCard deal={deal} />
							<DealInspectionCandidateDateCard
								deal={deal}
								handleMutate={handleMutate}
							/>
							<DealWitnessCard deal={deal} handleMutate={handleMutate} />
							{deal.dealType === "repair" && (
								<DealRepairCard
									companyType="client"
									deal={deal}
									handleMutate={handleMutate}
								/>
							)}
							<DealCard deal={deal} handleMutate={handleMutate} />
							<DealVisitDateClientCard />
							<DealConstructionScheduleClientCard deal={deal} />
						</div>
						<DealMain deal={deal} isAdmin={account.isAdmin} isVendor={false} />
					</div>
				</ClientDealLayout>
				{messages !== null && (
					<ChatPopContainer
						deal={deal}
						handleClose={() => {
							setIsOpen(false);
						}}
						handleMutate={handleMutateMessage}
						handleTemporaryUnread={handleTemporaryUnread}
						isAdmin={account.isAdmin}
						isOpen={isOpen}
						messages={messages}
						receiver="maintenance_company"
					/>
				)}
			</DefaultV2>
		</ItandiAccountContext.Provider>
	);
}
